<template>
  <div class="tw-w-3/5">
    <Header
      :title="$t('New job')"
      :link-text="$t('All jobs')"
      :link-url="laravelNamedRoute(namedRoutes.frontend.jobs.index.created)"
      :show-help-link="true"
    />
    <form @submit.prevent="onContinue">
      <WdsStack direction-column gap-custom="2.5rem" class="tw-mt-8">
        <WdsStack direction-column>
          <WdsText class="tw-mb-2">{{ $t('How would you like to hire?') }}</WdsText>

          <WdsStack direction-row gap-md>
            <FlowOption
              :active="flow === JOB_FLOW.BROADCAST"
              :title="$t('Find talent')"
              :info-text="
                $t(
                  'Share this job across multiple channels including your existing contacts, with staffing agencies, or publicly via the marketplace.',
                )
              "
              :tag-text="$t('Most flexible')"
              :icon="WdsIconNameEnum.USER_PROFILE"
              @click="handleBroadcast"
            />

            <FlowOption
              :active="flow === JOB_FLOW.DIRECT_HIRE"
              :title="$t('Direct hire')"
              :info-text="$t('Offer this job directly to a specific worker')"
              :tag-text="$t('Fastest')"
              tag-type="warning"
              :icon="WdsIconNameEnum.DASHBOARD"
              data-test="direct-hire-hiring-card"
              @click="handleDirectHire"
            />
          </WdsStack>
        </WdsStack>
        <div v-if="flow === JOB_FLOW.BROADCAST">
          <FeatureFlagged
            name="job-creation-flow-3"
            @enabled-updated="(enabled) => (newJobCreationFlowEnabled = enabled)"
          >
            <BaseSection>
              <BaseSectionBody>
                <BaseFieldGroup :label="$t('Job title')" full-width>
                  <div class="tw-w-full">
                    <WdsInput
                      v-model="name"
                      :placeholder="$t('e.g. Senior Front-end Engineer')"
                      data-test="job-title"
                      required
                      :error="broadcastNameError"
                    />
                  </div>
                </BaseFieldGroup>

                <BaseFieldGroup
                  :label="$t('Job owners')"
                  :helper-text="$t('Who is responsible for hiring this job?')"
                >
                  <div class="tw-w-full">
                    <WdsAvatarGroup
                      sm
                      :max-avatars="15"
                      class="tw-float-right tw-mb-sm"
                      :class="{ 'tw-invisible': jobOwnersAvatars.length === 0 }"
                      :avatars="jobOwnersAvatars.length ? jobOwnersAvatars : [{ path: '' }]"
                    />

                    <WdsSelect
                      v-model="jobOwners"
                      multi-select
                      :list="teamMembersAndOwners"
                      :placeholder="$t('Add/remove owners...')"
                      prefix-icon="search"
                      searchable
                      class="tw-w-full"
                    >
                      <template #option="{ option }">
                        <WdsStack align-center gap-sm>
                          <WdsAvatar xs :path="getAvatarPath(option.avatar, option.value)" />
                          <div>{{ option.label }}</div>
                        </WdsStack>
                      </template>
                      <template #header="{ options }">
                        <span v-if="options.length">
                          {{ options.length + ' selected' }}
                        </span>
                        <span v-else>
                          {{ $t('Add/remove owners...') }}
                        </span>
                      </template>
                    </WdsSelect>
                  </div>
                </BaseFieldGroup>
              </BaseSectionBody>
            </BaseSection>
          </FeatureFlagged>
        </div>
        <div v-if="flow === JOB_FLOW.DIRECT_HIRE">
          <WdsText class="tw-mb-2">
            <div v-if="!trustedContact?.workerId">
              {{ $t('Who would you like to hire?') }}
            </div>
            <div v-else>
              {{ $t('Worker') }}
            </div>
          </WdsText>
          <button
            class="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-p-3 tw-border tw-rounded tw-border-solid tw-border-gray-200 tw-w-full tw-text-left tw-bg-white focus-visible:tw-border-indigo-500 focus-visible:tw-focus-outline"
            :class="{ 'tw-border-red-500': !!trustedContactError }"
            data-test="trigger-trusted-contact-modal"
            type="button"
            @click="toggleTalentModal(!showTalentModal)"
          >
            <div v-if="!trustedContact?.workerId" class="tw-flex">
              <div
                class="tw-text-gray-400 tw-flex tw-items-center tw-justify-center tw-w-[40px] tw-h-[40px] tw-rounded-full tw-border tw-border-dashed tw-border-gray-400 tw-mr-3"
              >
                CW
              </div>
              <WdsText :sub-text="$t('Choose worker...')" :error="!!trustedContactError">
                {{ $t('Select or invite a trusted contact') }}
              </WdsText>
            </div>
            <div v-else>
              <WdsAvatarLabel
                :text="trustedContact?.name"
                :sub-text="trustedContact?.title"
                :path="getAvatarPath(trustedContact?.avatar, trustedContact?.workerId)"
              ></WdsAvatarLabel>
            </div>
            <WdsIcon class="tw-text-gray-600" :name="WdsIconNameEnum.CHEVRON_DOWN" />
          </button>
          <Teleport to="#modals">
            <WdsModal
              lg
              :show="showTalentModal"
              :title="$t('Direct Hire')"
              data-test="trusted-contact-modal"
              tabindex="0"
              @cancel="toggleTalentModal(false)"
            >
              <template #header>
                <div>
                  <WdsHeading>{{ $t('Direct Hire') }}</WdsHeading>
                  <WdsText large class="tw-mt-2">
                    {{ $t('Offer new job to specific worker') }}
                  </WdsText>
                </div>
              </template>
              <TalentSelectionDirectHire
                v-model:trusted-contact="trustedContact"
                @cancel="toggleTalentModal(false)"
                @confirm="toggleTalentModal(false)"
              />
            </WdsModal>
          </Teleport>
        </div>

        <WdsStack v-if="flow" align-center justify-end gap-md>
          <WdsSpinner v-if="loading" indeterminate data-test="saving-spinner" />

          <WdsTooltip v-if="hasAnyErrors" :text="$t('Missing required information')">
            <WdsButton disabled>
              {{ $t('Continue') }}
            </WdsButton>
          </WdsTooltip>
          <WdsButton
            v-else
            data-test="continue-hiring"
            primary
            :disabled="loading"
            @click="onContinue"
          >
            {{ $t('Continue') }}
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  WdsAvatar,
  WdsAvatarGroup,
  WdsAvatarLabel,
  WdsButton,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsInput,
  WdsModal,
  WdsSelect,
  WdsSpinner,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { useMutation } from '@vue/apollo-composable'
import { required } from '@vuelidate/validators'

import { $t } from '@/composables/translation'
import useApolloAndVuelidate from '@/composables/useApolloAndVuelidate'
import useApolloError from '@/composables/useApolloError'
import useCustomVuelidate from '@/composables/useCustomVuelidate'
import BaseSection from '@/components/BaseSection.vue'
import BaseSectionBody from '@/components/BaseSectionBody.vue'
import BaseFieldGroup from '@/components/BaseFieldGroup.vue'
import FeatureFlagged from '@/components/FeatureFlagged.vue'
import { getAvatarPath } from '@/utils/avatar'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { NetworkError } from '@/utils/notification'

import { JOB_FLOW } from '@/components/Job/constants'
import Header from '@/components/Job/Header.vue'
import { TrustedContact } from '@/components/Job/hiring-types'
import { CreateJobMutation } from '@/components/Job/graphql/mutations'
import FlowOption from '@/components/Job/Create/FlowOption.vue'
import TalentSelectionDirectHire from '@/components/Job/Create/TalentSelectionDirectHire.vue'

defineEmits(['showForm'])

const props = defineProps({
  companyId: {
    type: String,
    required: true,
  },
  teamMembersAndOwners: {
    type: Array,
    required: true,
  },
})

const { mutate: mutateCreateJob, error: errorCreateJob } = useMutation(CreateJobMutation, {
  clientId: 'publicApi',
})

const loading = ref<boolean>(false)
const newJobCreationFlowEnabled = ref<boolean>(false)

const name = ref<string>('')
const jobOwners = ref<
  {
    avatar: string
    value: string
  }[]
>([
  {
    value: currentUser.globalId,
    avatar: currentUser.avatar ?? null,
  },
])

const jobOwnersAvatars = computed(() =>
  jobOwners.value.map((jobOwner) => ({
    path: getAvatarPath(jobOwner.avatar, jobOwner.value),
  })),
)

const flow = ref<JOB_FLOW | null>(null)
const trustedContact = ref<TrustedContact | undefined>(undefined)
const showTalentModal = ref<boolean>(false)

const createPayload = computed(() => ({
  company: props.companyId,
  name: name.value,
  owners: jobOwners.value.map(({ value }) => value),
  skills: [],
}))

const directHirePayload = computed(() => ({
  company: props.companyId,
  trustedContact: trustedContact.value,
}))

const {
  error: directHireError,
  trustedContact: trustedContactError,
  touchAll: directHireToucheAll,
  resetAll: directHireResetAll,
} = useCustomVuelidate(
  {
    company: { required },
    trustedContact: { required },
  },
  directHirePayload,
)

const broadcastVul = useCustomVuelidate(
  {
    name: { required },
  },
  createPayload,
)

const {
  error: broadcastError,
  touchAll: broadcastTouchAll,
  resetAll: broadcastResetAll,
} = broadcastVul

const { name: broadcastNameError } = useApolloAndVuelidate(
  useApolloError(errorCreateJob),
  broadcastVul,
  'input',
)

const hasAnyErrors = computed(() => directHireError.value || broadcastError.value)

const toggleTalentModal = (show: boolean) => {
  showTalentModal.value = show
}

const onContinue = async () => {
  if (flow.value === JOB_FLOW.DIRECT_HIRE) {
    directHireToucheAll()

    if (directHireError.value) return

    window.location.href = laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect, {
      freelancer: trustedContact.value?.workerId,
    })
    return
  }

  if (flow.value === JOB_FLOW.BROADCAST) {
    if (!newJobCreationFlowEnabled.value) {
      window.location.href = laravelNamedRoute(namedRoutes.frontend.job.create)
      return
    }

    broadcastTouchAll()

    if (broadcastError.value) return

    try {
      loading.value = true

      const result = await mutateCreateJob({
        input: createPayload.value,
      })

      if (result?.data?.createJob?.id) {
        window.location.href = laravelNamedRoute(namedRoutes.yjob.edit, {
          yjob: result.data.createJob.id,
        })
      }
    } catch (ex: any) {
      new NetworkError({
        ex,
        msg: $t('Something went wrong when trying to create job ":name" ', { name: name.value }),
      }).dispatch()

      loading.value = false
    }
  }
}

const handleBroadcast = () => {
  flow.value = JOB_FLOW.BROADCAST
  directHireResetAll()
}

const handleDirectHire = () => {
  flow.value = JOB_FLOW.DIRECT_HIRE
  broadcastResetAll()
}
</script>

<style scoped lang="scss">
:deep(.wds-input-label) {
  @apply tw-mb-0;
}

:deep(.wds-modal-header) {
  @apply tw-items-start;
}
</style>
